import {
  AlertIcon, CheckIcon, CircleSlashIcon, ClockIcon, GitMergeIcon, GitPullRequestIcon, HistoryIcon, PlayIcon, VerifiedIcon, XIcon,
} from "@primer/octicons-react";
import type { AuditEntry, ChangeState } from "../api/types";
import { cn } from "../lib/utils";
import { Tooltip } from "./ui/tooltip";

/** The happy path every change walks; failures stop it at some step. */
const STEPS: Array<{ state: ChangeState; label: string; Icon: typeof CheckIcon }> = [
  { state: "created", label: "Opened", Icon: GitPullRequestIcon },
  { state: "ci", label: "CI", Icon: PlayIcon },
  { state: "ready", label: "Queued", Icon: ClockIcon },
  { state: "landing", label: "Landing", Icon: GitMergeIcon },
  { state: "landed", label: "Landed", Icon: GitMergeIcon },
  { state: "verified", label: "Verified", Icon: VerifiedIcon },
];

const FAILED: Partial<Record<ChangeState, { label: string; Icon: typeof CheckIcon }>> = {
  rejected: { label: "Rejected", Icon: XIcon },
  conflict: { label: "Conflict", Icon: AlertIcon },
  cancelled: { label: "Cancelled", Icon: CircleSlashIcon },
  reverted: { label: "Reverted", Icon: HistoryIcon },
};

type StepStatus = "done" | "current" | "failed" | "todo";

/** The merge train is how a change lands when the repo enables it: drawn as the Landing step.
 *  Waiting for human review is drawn on the Queued step it goes to once approved. */
const step = (s: string) => (s === "train" ? "landing" : s === "review" ? "ready" : s);

const at = (s: ChangeState) => STEPS.findIndex((x) => x.state === step(s));

/** Where a failure is drawn when there is no audit trail to say which step it happened in. */
const FAILED_AT: Partial<Record<ChangeState, number>> = { rejected: at("ci"), conflict: at("landing"), cancelled: at("created"), reverted: at("verified") };

/**
 * Where a change is on the path. With the audit trail we know exactly which steps were
 * reached; without it we infer from the state. A failure is drawn on the step the change
 * was in when it stopped; a revert fails verification.
 */
function progress(state: ChangeState, audit?: AuditEntry[]): StepStatus[] {
  const reached = audit && new Set(audit.map((a) => auditState(a.action)));
  let current = at(state);
  let failed = -1;
  if (current < 0) {
    if (state === "reverted") failed = at("verified");
    else if (reached) STEPS.forEach((s, i) => reached.has(s.state) && (failed = i));
    else failed = FAILED_AT[state] ?? 0;
    current = failed;
  }
  return STEPS.map((_, i) => {
    if (i === failed) return "failed";
    if (i === current) return state === "landed" ? "done" : "current";
    return i > current ? "todo" : "done";
  });
}

/** The state an audit action moved to; changes from before the single CI state logged ci_tier1/ci_tier2. */
const auditState = (action: string) => {
  const s = action.replace("change.", "");
  return s.startsWith("ci_tier") ? "ci" : step(s);
};

const DOT: Record<StepStatus, string> = { done: "bg-success-emphasis", current: "bg-warning ring-2 ring-warning/30", failed: "bg-danger-emphasis", todo: "bg-neutral-muted" };

/** The lifecycle as one line of labelled dots, for a header that leads with a status sentence. */
export function CompactStepper({ state, audit }: { state: ChangeState; audit?: AuditEntry[] }) {
  const p = progress(state, audit);
  const fail = FAILED[state];
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-1 text-xs text-muted-foreground" aria-label="Change lifecycle">
      {STEPS.map((s, i) => (
        <li key={s.state} className="flex items-center gap-1" aria-current={p[i] === "current" ? "step" : undefined}>
          {i > 0 && <span className={cn("h-px w-4", p[i] === "todo" ? "bg-border" : "bg-success-emphasis")} />}
          <i className={cn("size-2 rounded-full", DOT[p[i]])} />
          <span className={cn(p[i] === "current" && "font-semibold text-foreground", p[i] === "failed" && "font-semibold text-danger")}>
            {p[i] === "failed" && fail ? fail.label : s.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

const MINI: Record<StepStatus, string> = { done: "bg-success-emphasis", current: "bg-warning", failed: "bg-danger-emphasis", todo: "bg-neutral-muted" };

export function MiniFlow({ state }: { state: ChangeState }) {
  const p = progress(state);
  return (
    <Tooltip content={`Lifecycle: ${state}`}>
      <span className="inline-flex items-center gap-0.5">
        {p.map((st, i) => (
          <i key={i} className={cn("h-1 w-3 rounded-xs", MINI[st])} />
        ))}
      </span>
    </Tooltip>
  );
}
